import { EditOutlined } from '@ant-design/icons';
import { Badge, Popconfirm } from 'antd';
import { useLazyGetAllAbsenceQuery, useTakeToWorkAbsenceMutation } from 'app/services/edpApiV1/absence';
import { viewerModel } from 'entities/viewer';
import { TablePull } from 'features/tables';
import React, { useEffect, useState } from 'react';
import { RolesUser, SortCondition } from 'shared/enums';
import { dataForAbsencesTable, getErrorData, sortingDate } from 'shared/helpers/utils';
import { usePullSort } from 'shared/hooks';
import { useNotifications } from 'shared/hooks/use-notifications';
import { RequestsSearch, TColumns } from 'shared/types/types';
import { useAppSelector } from 'app/store';
import { useIndicators } from 'features/indicators';
import { ONE_MINUTE } from 'shared/constants';
import { TPreparedAbsenceData } from 'shared/types/absence.types';
import { SearchKeysNews } from 'shared/enums/news';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import ModalAbsenceSubordinates from '../modalAbsenceSubordinates';
import style from './index.module.scss';

const AbsencePull = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [dataAbsences, setDataAbsences] = useState<TPreparedAbsenceData[] | []>([]);
  const [requestId, setRequestId] = useState<number>();
  const [openModal, setOpenModal] = useState(false);
  const [sortInfo, trigger] = usePullSort();
  const { role } = useAppSelector(viewerModel.me.selectors.selectViewerProfile);
  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    } else {
      setSearch({ [key]: data ? data : undefined });
    }
  };

  // Добавляем пуллинг обновления списка и обновляем индикаторы через хук useIndicators
  const [getAllAbsenceTrigger, resultAllAbsence] = useLazyGetAllAbsenceQuery({ pollingInterval: ONE_MINUTE });

  const [absenceTakeToWork, resultToWork] = useTakeToWorkAbsenceMutation({
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
  });
  const { isSuccess, isError, error, reset } = resultToWork;

  const handleToWork = (id: string | number): void => {
    absenceTakeToWork(Number(id));
  };

  const handleChangeResponsible = (id: number): void => {
    setRequestId(id);
    setOpenModal(true);
  };

  const columns: TColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'key',
      width: 120,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: _ => ({
        onClick: () => trigger(),
      }),
    },
    {
      title: 'Период',
      dataIndex: 'period',
      key: 'period',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <span>
          <Badge status={status.type} />
          {status.name}
        </span>
      ),
    },
    {
      title: 'Действие',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record: { key: React.Key }) => (
        <Popconfirm placement="leftTop" title="Вы уверены?" onConfirm={() => handleToWork(record.key)}>
          <a>Взять в работу</a>
        </Popconfirm>
      ),
    },
  ];

  const columnsWithSubordinates: TColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'key',
      width: 120,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: _ => ({
        onClick: () => trigger(),
      }),
    },
    {
      title: 'Период',
      dataIndex: 'period',
      key: 'period',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <span>
          <Badge status={status.type} />
          {status.name}
        </span>
      ),
    },
    {
      title: 'Действие',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record: { key: React.Key }) => (
        <Popconfirm placement="leftTop" title="Вы уверены?" onConfirm={() => handleToWork(record.key)}>
          <a>Взять в работу</a>
        </Popconfirm>
      ),
    },
  ];

  if (!allAvailableOrganizations && role === RolesUser.HRDirector) {
    columnsWithSubordinates.push({
      title: 'Ответственный',
      dataIndex: 'hr',
      key: 'hr',
      render: (hr, record: { key: React.Key }) => {
        return (
          <>
            {hr && (hr.name || hr.middleName || hr.lastName) ? (
              <>
                <span>{`${hr.name} ${hr.middleName ?? ''} ${hr.lastName}`}</span>
                <EditOutlined
                  className={style.btnChangeResponsible}
                  onClick={() => handleChangeResponsible(Number(record.key))}
                />
              </>
            ) : (
              <span className={style.changeResponsible} onClick={() => handleChangeResponsible(Number(record.key))}>
                Назначить
              </span>
            )}
          </>
        );
      },
    });
  }
  useEffect(() => {
    if (sortInfo.value === null)
      getAllAbsenceTrigger({ type: 'new', page: currentPage, size: currentSize, allAvailableOrganizations, ...search });
    if (sortInfo.value === SortCondition.ASCEND)
      getAllAbsenceTrigger({
        type: 'new',
        page: currentPage,
        size: currentSize,
        order: SortCondition.ASC,
        allAvailableOrganizations,
        ...search,
      });
    if (sortInfo.value === SortCondition.DESCEND)
      getAllAbsenceTrigger({
        type: 'new',
        page: currentPage,
        size: currentSize,
        order: SortCondition.DESC,
        allAvailableOrganizations,
        ...search,
      });
  }, [getAllAbsenceTrigger, currentPage, currentSize, sortInfo.value, search, allAvailableOrganizations]);

  useEffect(() => {
    if (resultAllAbsence && resultAllAbsence?.data?.data) {
      const dataTable = dataForAbsencesTable(resultAllAbsence?.data?.data);
      if (dataTable?.length) {
        setDataAbsences(dataTable);
      } else {
        setDataAbsences([]);
      }
    }
  }, [resultAllAbsence]);

  // подписываемся на обновление индикаторов, после обновления списка и отключаем пуллинг звпроса индикаторов
  useIndicators(resultAllAbsence.isSuccess, resultAllAbsence.isFetching, false);

  useNotifications(isSuccess, isError, 'Заявка взята в работу', error?.message, reset);

  useEffect(() => {
    const paginationInfo = resultAllAbsence?.data?.pagination;
    if (dataAbsences?.length === 0 && paginationInfo && currentPage !== 1) {
      // определяю текущую страницу
      const totalPages = Math.ceil(paginationInfo.total / paginationInfo.size);
      //  устанавливает текущую страницу на минимальное значение из текущей страницы и общего количества страниц,
      //  чтобы избежать установки текущей страницы на значение, превышающее общее количество страниц.
      setCurrentPage(Math.min(currentPage, totalPages));
    }
  }, [dataAbsences, resultAllAbsence]);

  return (
    <>
      {dataAbsences && (
        <TablePull
          dataSource={dataAbsences}
          columns={role === RolesUser.HRDirector || role === RolesUser.SecondLine ? columnsWithSubordinates : columns}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          total={resultAllAbsence?.data?.pagination?.total}
          setCurrentSize={setCurrentSize}
          currentSize={currentSize}
          onSearch={onSearch}
        />
      )}
      {requestId && (
        <ModalAbsenceSubordinates openModal={openModal} setOpenModal={setOpenModal} requestId={requestId} />
      )}
    </>
  );
};

export default AbsencePull;
